import { type ReactNode, createContext, useContext, useCallback, useEffect, useMemo, useRef } from 'react'
import { type HistorySelectItem, useAddHistorySelectedItems } from '@renderer/overlay/Sidepanel/stores/history-select'
import type { RecentActivity } from '@renderer/overlay/Sidepanel/types'

type HistorySelectContextType = {
  getLastSelectedIndex: () => number | null
  setLastSelectedIndex: (index: number | null) => void
  handleRangeSelect: (startIndex: number, endIndex: number) => void
}

const HistorySelectContext = createContext<HistorySelectContextType | undefined>(undefined)

/**
 * Shift-range selection support. The item list and last-selected index live in
 * refs (read at click time, never during render), so the context value is
 * referentially stable forever — selecting a row or a list update never
 * re-renders the other visible checkboxes through this context. Per-row
 * selection state comes from per-item atoms in the history-select store.
 */
export function HistorySelectProvider({ children, items }: { children: ReactNode; items: Array<RecentActivity> }) {
  const addHistorySelectedItems = useAddHistorySelectedItems()

  const itemsRef = useRef(items)
  itemsRef.current = items

  const lastSelectedIndexRef = useRef<number | null>(null)

  // Reset the anchor when the set of items actually changes (filter/search/
  // data), so a stale index can't range-select across a reordered list.
  const prevItemIdsRef = useRef<string | null>(null)
  useEffect(() => {
    const ids = items.map((i) => `${i.type}:${i.id}`).join(',')
    if (prevItemIdsRef.current !== null && prevItemIdsRef.current !== ids) {
      lastSelectedIndexRef.current = null
    }
    prevItemIdsRef.current = ids
  }, [items])

  const getLastSelectedIndex = useCallback(() => lastSelectedIndexRef.current, [])
  const setLastSelectedIndex = useCallback((index: number | null) => {
    lastSelectedIndexRef.current = index
  }, [])

  const handleRangeSelect = useCallback(
    (startIndex: number, endIndex: number) => {
      const currentItems = itemsRef.current
      const minIndex = Math.min(startIndex, endIndex)
      const maxIndex = Math.max(startIndex, endIndex)

      const itemsToSelect: Array<HistorySelectItem> = []
      for (let i = minIndex; i <= maxIndex && i < currentItems.length; i++) {
        const item = currentItems[i]
        if (item && (item.type === 'chat' || item.type === 'audio-note' || item.type === 'completed-action')) {
          itemsToSelect.push({ id: item.id, type: item.type })
        }
      }

      if (itemsToSelect.length > 0) {
        addHistorySelectedItems(itemsToSelect)
      }

      lastSelectedIndexRef.current = endIndex
    },
    [addHistorySelectedItems],
  )

  const value = useMemo(
    () => ({ getLastSelectedIndex, setLastSelectedIndex, handleRangeSelect }),
    [getLastSelectedIndex, setLastSelectedIndex, handleRangeSelect],
  )

  return <HistorySelectContext.Provider value={value}>{children}</HistorySelectContext.Provider>
}

export function useHistorySelectContext() {
  const context = useContext(HistorySelectContext)
  if (!context) {
    throw new Error('useHistorySelectContext must be used within a HistorySelectProvider')
  }
  return context
}
